import type { MeasurementSystem } from './distance';

// Should be generated by the backend?
// should we use the SupportedCountry from 'generated/graphql', same as the one in graphql?
export const SupportedCountries = ['NZ', 'AU', 'US'] as const;
export type SupportedCountry = (typeof SupportedCountries)[number];

/**
 * An ISO code for a country an address can sit in. Wider than `SupportedCountry`, which is
 * an ArchiPro market: professionals on an existing market hold locations in countries
 * ArchiPro does not trade in, and those have no domain, currency or locale of their own.
 *
 * Deliberately not a union of codes. A country becomes addressable by importing its place
 * data, so the set is decided at runtime by `Archipro\Component\Country\AddressCountry` and
 * reaches the front end through the `addressCountries` query — a union here could only ever
 * be a stale copy of it, and narrowed away countries the server already served.
 */
export type AddressCountry = string;

export interface CountryInfo {
    shortName: string;
    name: string;
    nameCasual: string;
    domain: string;
    /**
     * Primary content language for the market as an ISO 639-1 code (e.g. `en`).
     * All markets are English-only today; this is the single input from which
     * hreflang (`en-us`) and og:locale (`en_US`) are derived, so adding a
     * future non-English market is a config change rather than a code change.
     */
    language: string;
    /** Unicode regional-indicator flag for UI menus. */
    flagEmoji: string;
}

export type ConsentMode = 'none' | 'notice' | 'opt-in';

export interface CountryConfig {
    consentMode: ConsentMode;
    honourGlobalPrivacyControl: boolean;
    phoneCountryCode: string | null;
    allowProducts: boolean;
    allowMemberGuides: boolean;
    allowMemberOrders: boolean;
    allowMemberTickets: boolean;
    showHDENavLink: boolean;
    measurementSystem: MeasurementSystem;
}

export interface LocalisationData {
    locales: string[];
    timezone: string;
    country: SupportedCountry;
    countryConfig: CountryConfig;
}
